import {
  formatMMSS,
  parisIntervalToDuration,
  secondsToDuration,
} from 'utils/durationUtils';
import { RefObject } from 'react';
import { ROUTES } from 'utils/constants/constants_routes';
import { SteloEventDatesType } from 'pages/Stelo/useStelo';
import smartlookClient from 'smartlook-client';

export const STELO_UNFINISHED_EVENT = 'STELO_UNFINISHED';

// Maps raw Stelo iframe event names to standardized tracking event names
export const steloEventsMap: { [key: string]: string } = {
  // STEPS
  'application:ready': '1_STELO_READY_SCREEN',
  'execution:workflow:loaded': '2_STELO_LOAD_FORM',
  'stelo_name_execution:workflow:loaded': '2_1_STELO_LOAD',
  'execution:workflow:step:uploaddocument:oa:showrequestdataform':
    '3_STELO_FORM',
  'execution:workflow:step:uploaddocument:oa:requestdataformfilled':
    '4_STELO_FORM_FILLED',
  'execution:workflow:step:uploaddocument:oa:showrequestpinform:SEGURIDAD_SOCIAL':
    '5_STELO_PIN_LOADING_REQUEST',
  'execution:workflow:step:uploaddocument:oa:requestpinform:SEGURIDAD_SOCIAL':
    '6_STELO_PIN_SCREEN',
  'execution:workflow:step:uploaddocument:oa:connected:sms:SEGURIDAD_SOCIAL':
    '7_STELO_CONNECT_LOADING_SMS',
  'execution:workflow:step:uploaddocument:oa:connected:app:SEGURIDAD_SOCIAL':
    '7_2_STELO_CONNECT_APP',
  'execution:workflow:step:uploaddocument:oa:requestdownloaddata:SEGURIDAD_SOCIAL':
    '8_STELO_DOWNLOAD_DATA_SS',
  'execution:workflow:step:uploaddocument:oa:downloadingdata':
    '9_STELO_DOWNLOADING_DATA',
  'execution:workflow:step:uploaddocument:oa:downlaodeddata':
    '10_STELO_DOWNLOAD_DATA',
  'execution:workflow.step.apicallandanalysis:loaded':
    '11_STELO_ANALYSIS_LOADING',
  'execution:workflow.step.apicallandanalysis.apicall:completed':
    '12_STELO_COMPLETED_CALL_SCREEN',
  'execution:workflow.step.apicallandanalysis.analysis:completed':
    '13_STELO_COMPLETED_ANALYSIS_SCREEN',
  'execution:workflow:complete': '14_STELO_COMPLETED_SCREEN',
  'stelo_name_execution:workflow:complete': '15_STELO_COMPLETED',
  // ERRORS
  'execution:workflow:step:uploaddocument:oa:pinerror:SEGURIDAD_SOCIAL':
    '001_STELO_PIN_ERROR',
  'execution:workflow:step:uploaddocument:oa:connecterror:SEGURIDAD_SOCIAL':
    '002_STELO_CONNECT_ERROR',
  'execution:workflow:step:uploaddocument:oa:downloaderror':
    '003_STELO_DOWNLOAD_ERROR',
  'execution:workflow.step.apicallandanalysis.apicall:errors':
    '004_STELO_CALL_ERROR',
  'execution:workflow:step:uploaddocument:oa:sendpinerror:seguridad_social':
    '005_STELO_SEND_PIN_ERROR',
  'stelo_name_execution:workflow.step.apicallandanalysis.apicall:errors':
    '006_STELO_ANALYSIS_ERROR',
};

const ERROR_STELO_EVENTS = [
  '001_STELO_PIN_ERROR',
  '002_STELO_CONNECT_ERROR',
  '003_STELO_DOWNLOAD_ERROR',
  '004_STELO_CALL_ERROR',
  '005_STELO_SEND_PIN_ERROR',
  '006_STELO_ANALYSIS_ERROR',
];

// Events triggered by user actions (PIN entry, SMS connection) — screen time counts as user time
const POST_SCREEN_USER_STELO_EVENTS = [
  '5_STELO_PIN_LOADING_REQUEST',
  '7_STELO_CONNECT_LOADING_SMS',
];

// Events triggered after a loading/processing phase — screen time counts as loading time
const POST_SCREEN_LOADING_STELO_EVENTS = [
  '6_STELO_PIN_SCREEN',
  '8_STELO_DOWNLOAD_DATA_SS',
  '10_STELO_DOWNLOAD_DATA',
  '12_STELO_COMPLETED_CALL_SCREEN',
  '14_STELO_COMPLETED_SCREEN',
  ...ERROR_STELO_EVENTS,
];

// Events that mark the end of the Stelo flow (completed or abandoned)
const FINAL_STELO_EVENTS = [
  '14_STELO_COMPLETED_SCREEN',
  STELO_UNFINISHED_EVENT,
];

// Union of all events after which the current screen timer should be evaluated
const SCREEN_TOTAL_STELO_EVENTS = [
  ...POST_SCREEN_USER_STELO_EVENTS,
  ...POST_SCREEN_LOADING_STELO_EVENTS,
];

export const sendSteloEventsNames = (
  steloEventName: string,
  dataEventsRef: RefObject<SteloEventDatesType>,
) => {
  // Fall back to the raw event name if it has no mapping
  const eventNameParsed = steloEventsMap[steloEventName] || steloEventName;

  const eventDetails = {
    prevTimeScreen: 'NA',
    timeFlow: 'NA',
    timeLoading: 'NA',
    flowFinished: dataEventsRef.current.flowFinished,
    isApproved: window.location.pathname === ROUTES.STELO_APPROVED,
    route: window.location.pathname,
  };

  // Initialize flow and screen start timestamps on the first event
  if (eventNameParsed === '1_STELO_READY_SCREEN') {
    dataEventsRef.current.dateStartFlow = new Date();
    dataEventsRef.current.dateStartScreen = new Date();
  }

  if (SCREEN_TOTAL_STELO_EVENTS.includes(eventNameParsed)) {
    // Calculate time spent on the current screen
    const screenTime = parisIntervalToDuration(
      dataEventsRef.current.dateStartScreen || new Date(),
      new Date(),
    );

    eventDetails.prevTimeScreen = formatMMSS(screenTime);

    if (POST_SCREEN_LOADING_STELO_EVENTS.includes(eventNameParsed)) {
      // Accumulate loading time in seconds across all loading screens
      dataEventsRef.current.secondsLoadingTime +=
        (screenTime.minutes || 0) * 60 + (screenTime.seconds || 0);
      eventDetails.timeLoading = formatMMSS(
        secondsToDuration(dataEventsRef.current.secondsLoadingTime),
      );
    }

    // Reset screen start time for the next screen
    dataEventsRef.current.dateStartScreen = new Date();
  }

  // Calculate total elapsed time since the beginning of the flow
  const flowTime = parisIntervalToDuration(
    dataEventsRef.current.dateStartFlow || new Date(),
    new Date(),
  );
  eventDetails.timeFlow = formatMMSS(flowTime);

  // On final events, report the total accumulated loading time
  if (FINAL_STELO_EVENTS.includes(eventNameParsed)) {
    eventDetails.timeLoading = formatMMSS(
      secondsToDuration(dataEventsRef.current.secondsLoadingTime),
    );
  }

  // Mark flow as complete when the success screen is reached
  if (eventNameParsed === '14_STELO_COMPLETED_SCREEN') {
    eventDetails.flowFinished = true;
    dataEventsRef.current.flowFinished = true;
  }

  // Send a single STELO_ERROR event to avoid duplicate error tracking
  if (ERROR_STELO_EVENTS.includes(eventNameParsed)) {
    if (!dataEventsRef.current.customErrorEventSent)
      smartlookClient.track('STELO_ERROR', eventDetails);
    dataEventsRef.current.customErrorEventSent = true;
  }

  smartlookClient.track(eventNameParsed, eventDetails);
};
